import { useQuery } from "@tanstack/react-query"
import { Badge } from "design-system/atoms/Badge"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { capitalize, formatDistanceLang } from "lopermut"
import { ArrowUpRight } from "lucide-react"
import { shopifyIntegrationQuery } from "modules/settings/lib/shopify/shopifyIntegrationQuery"
import { TagBadge } from "modules/customers/components/Tags/TagBadge"
import { ShopifyIcon } from "modules/shared/icons/shopify"
import { useTranslation } from "react-i18next"
import { ShopifyCustomerResponseDataSchema } from "schema"
import { z } from "zod"

type Props = {
  shopifyCustomer: z.infer<typeof ShopifyCustomerResponseDataSchema>
  tagNames: string[]
}

const MARKETING_STATE_VARIANTS: Record<string, "success" | "negative" | "warning"> = {
  SUBSCRIBED: "success",
  UNSUBSCRIBED: "negative",
  INVALID: "negative",
  PENDING: "warning",
}

export function ShopifyProfile({ shopifyCustomer, tagNames }: Props) {
  const { t } = useTranslation()
  const { data: shopifyIntegration } = useQuery(shopifyIntegrationQuery())

  const shopHandle = shopifyIntegration?.shopifyUrl
    ?.replace(/^https?:\/\//, "")
    .replace(/\.myshopify\.com\/?$/, "")
  const adminUrl = shopHandle
    ? `https://admin.shopify.com/store/${shopHandle}/customers/${shopifyCustomer.externalId}`
    : null

  const humanize = (value: string) => capitalize(value.replaceAll("_", " ")) ?? value

  const rfmGroupKey = shopifyCustomer.rfmGroup
    ?.toLowerCase()
    .replace(/_(\w)/g, (_, letter: string) => letter.toUpperCase())

  const channels = [
    {
      label: t("customers.shopify.emailMarketing"),
      state: shopifyCustomer.emailMarketingState,
    },
    {
      label: t("customers.shopify.smsMarketing"),
      state: shopifyCustomer.smsMarketingState,
    },
    {
      label: t("customers.shopify.whatsappMarketing"),
      state: shopifyCustomer.whatsappMarketingState,
    },
  ]

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border px-4 py-3">
      <div className="flex flex-row flex-wrap items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            {adminUrl ? (
              <a
                href={adminUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-sm font-medium hover:underline"
              >
                <ShopifyIcon className="size-4 -translate-y-px" />
                {t("customers.shopify.title")}
                <ArrowUpRight className="size-3.5 text-muted-foreground" />
              </a>
            ) : (
              <span className="flex items-center gap-1.5 text-sm font-medium">
                <ShopifyIcon className="size-4 -translate-y-px" />
                {t("customers.shopify.title")}
              </span>
            )}
          </TooltipTrigger>
          <TooltipContent>
            {t("customers.shopify.idAndSync", {
              externalId: shopifyCustomer.externalId,
              distance: formatDistanceLang(shopifyCustomer.updatedAt, new Date()),
            })}
          </TooltipContent>
        </Tooltip>
        <div className="ml-auto flex flex-row flex-wrap items-center gap-2">
          {shopifyCustomer.rfmGroup && (
            <Badge variant="outline">
              {t(`segments.predefined.kind.rfm.segments.${rfmGroupKey}`, {
                defaultValue: humanize(shopifyCustomer.rfmGroup),
              })}
            </Badge>
          )}
          {shopifyCustomer.taxExempt && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Badge variant="secondary">{t("customers.shopify.taxExempt")}</Badge>
                </span>
              </TooltipTrigger>
              {shopifyCustomer.taxExemptions.length > 0 && (
                <TooltipContent>
                  {shopifyCustomer.taxExemptions.map(humanize).join(", ")}
                </TooltipContent>
              )}
            </Tooltip>
          )}
        </div>
      </div>
      <span className="text-sm text-muted-foreground">
        {shopifyCustomer.lastOrderAt
          ? t("customers.shopify.ordersWithLast", {
              count: shopifyCustomer.ordersCount ?? 0,
              distance: formatDistanceLang(shopifyCustomer.lastOrderAt, new Date()),
            })
          : t("customers.shopify.noOrders")}
      </span>
      <div className="flex flex-col gap-2">
        {channels.map((channel) => (
          <div key={channel.label} className="flex flex-row items-center justify-between gap-3">
            <span className="text-sm text-muted-foreground">{channel.label}</span>
            {channel.state ? (
              <Badge variant={MARKETING_STATE_VARIANTS[channel.state] ?? "cached"}>
                {t(`customers.shopify.marketingStates.${channel.state}`, {
                  defaultValue: humanize(channel.state),
                })}
              </Badge>
            ) : (
              <span className="text-sm text-muted-foreground">{"-"}</span>
            )}
          </div>
        ))}
      </div>
      {tagNames.length > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex flex-wrap items-center gap-1.5">
              {tagNames.map((name) => (
                <TagBadge key={name} name={name} />
              ))}
            </div>
          </TooltipTrigger>
          <TooltipContent>{t("customers.shopify.tagsManagedByShopify")}</TooltipContent>
        </Tooltip>
      )}
      {shopifyCustomer.note && (
        <p className="border-l-2 border-border pl-3 text-sm text-muted-foreground whitespace-pre-wrap">
          {shopifyCustomer.note}
        </p>
      )}
    </div>
  )
}
